/**
 * @fileoverview Monitor especializado para microservicio de sincronización
 * Implementa patrones Observer, Command y Strategy para monitoreo adaptativo
 */

import appConfig from '../infrastructure/config/AppConfig';

// Constantes de configuración
const HEALTH_CHECK_INTERVAL = 60000; // 1 minuto
const METRICS_STORAGE_KEY = 'nubelytics_sync_metrics';
const ALERT_THRESHOLDS = {
  responseTime: 2000, // ms
  errorRate: 0.05, // 5%
  availabilityMin: 0.99 // 99%
};

// Estado interno y métricas
let isMonitoring = false;
let healthCheckInterval = null;
let metricsBuffer = {
  requests: [],
  errors: [],
  responseTimes: []
};

// Callbacks registrados
const alertCallbacks = [];
const metricCallbacks = [];

/**
 * Monitor del servicio de sincronización con capacidades avanzadas
 * Implementa patrón Singleton para punto único de monitoreo
 */
class SyncServiceMonitor {
  constructor() {
    if (SyncServiceMonitor.instance) {
      return SyncServiceMonitor.instance;
    }
    
    this.lastStatus = null;
    this.startTime = Date.now();
    this.totalRequests = 0;
    this.totalErrors = 0;
    this.averageResponseTime = 0;
    
    // Recuperar métricas anteriores si existen
    this._loadPersistedMetrics();
    
    SyncServiceMonitor.instance = this;
  }
  
  /**
   * Inicia el monitoreo continuo del servicio de sincronización
   * @param {Object} options - Opciones de configuración
   * @param {boolean} options.persistMetrics - Si debe persistir métricas
   * @param {Function} options.onAlert - Callback para alertas
   */
  startMonitoring(options = {}) {
    if (isMonitoring) return;
    
    console.log('[SyncMonitor] Iniciando monitoreo del microservicio sync.nubelytics.com');
    
    // Registrar callback de alerta si se proporciona
    if (typeof options.onAlert === 'function') {
      this.onAlert(options.onAlert);
    }
    
    // Configurar persistencia
    this.persistMetrics = options.persistMetrics !== false;
    
    // Realizar verificación inmediata
    this._performHealthCheck();
    
    // Configurar verificaciones periódicas
    healthCheckInterval = setInterval(() => {
      this._performHealthCheck();
    }, HEALTH_CHECK_INTERVAL);
    
    isMonitoring = true;
    
    // Return this para encadenamiento de métodos
    return this;
  }
  
  /**
   * Detiene el monitoreo continuo
   */
  stopMonitoring() {
    if (!isMonitoring) return;
    
    clearInterval(healthCheckInterval);
    healthCheckInterval = null;
    isMonitoring = false;
    
    console.log('[SyncMonitor] Monitoreo detenido');
    return this;
  }
  
  /**
   * Registra callback para alertas - Patrón Observer
   * @param {Function} callback - Función a llamar en caso de alerta
   */
  onAlert(callback) {
    if (typeof callback === 'function' && !alertCallbacks.includes(callback)) {
      alertCallbacks.push(callback);
    }
    return this;
  }
  
  /**
   * Registra callback para métricas - Patrón Observer
   * @param {Function} callback - Función a llamar cuando se actualizan métricas
   */
  onMetricsUpdate(callback) {
    if (typeof callback === 'function' && !metricCallbacks.includes(callback)) {
      metricCallbacks.push(callback);
    }
    return this;
  }
  
  /**
   * Registra una petición al servicio
   * @param {Object} requestData - Datos de la petición
   */
  recordRequest(requestData) {
    this.totalRequests++;
    
    const start = Date.now();
    
    // Devolver función para registrar finalización
    return {
      success: (responseData) => {
        const duration = Date.now() - start;
        this._recordResponse(duration, requestData, responseData);
      },
      error: (errorData) => {
        const duration = Date.now() - start;
        this._recordError(duration, requestData, errorData);
      }
    };
  }
  
  /**
   * Obtiene métricas actuales del servicio
   * @returns {Object} Métricas actuales
   */
  getMetrics() {
    const uptime = Date.now() - this.startTime;
    const errorRate = this.totalRequests > 0 ? this.totalErrors / this.totalRequests : 0;
    
    return {
      status: this.lastStatus,
      uptime,
      totalRequests: this.totalRequests,
      totalErrors: this.totalErrors,
      errorRate,
      averageResponseTime: this.averageResponseTime,
      isCritical: errorRate > ALERT_THRESHOLDS.errorRate || 
                 (this.lastStatus && this.lastStatus !== 'healthy'),
      timestamp: Date.now()
    };
  }
  
  /**
   * Verifica manualmente el estado de salud del servicio
   * @returns {Promise<Object>} Resultado de la verificación
   */
  async checkHealth() {
    return await this._performHealthCheck();
  }
  
  // Métodos internos
  
  /**
   * Realiza verificación de salud del servicio - Patrón Command
   * @private
   */
  async _performHealthCheck() {
    try {
      const healthCheckUrl = appConfig.getSyncUrl('healthcheck');
      const start = Date.now();
      
      const response = await fetch(healthCheckUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-Monitor-Check': 'true'
        }
      });
      
      const duration = Date.now() - start;
      
      if (!response.ok) {
        throw new Error(`Healthcheck fallido: ${response.status}`);
      }
      
      let result;
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        try {
          result = await response.json();
        } catch (parseErr) {
          // Si falla el parseo, registrar y continuar
          console.warn('[SyncMonitor] Respuesta no JSON en healthcheck:', parseErr);
          result = { status: 'unknown' };
        }
      } else {
        // Contenido no JSON (p.e. HTML), considerar servicio operativo si código 200
        result = { status: 'ok' };
      }
      this.lastStatus = result.status || 'unknown';
      
      // Registrar tiempo de respuesta
      this._recordResponseTime(duration);
      
      // Notificar si es demasiado lento
      if (duration > ALERT_THRESHOLDS.responseTime) {
        this._notifyAlert({
          type: 'performance',
          message: `Tiempo de respuesta lento: ${duration}ms`,
          threshold: ALERT_THRESHOLDS.responseTime,
          value: duration
        });
      }
      
      // Actualizar métricas
      this._notifyMetricsUpdate();
      
      // Persistir métricas
      if (this.persistMetrics) {
        this._persistMetrics();
      }
      
      return { 
        status: this.lastStatus, 
        responseTime: duration,
        timestamp: Date.now()
      };
    } catch (error) {
      // Registrar error
      this.lastStatus = 'error';
      this.totalErrors++;
      
      // Notificar error
      this._notifyAlert({
        type: 'availability',
        message: `Error en healthcheck: ${error.message}`,
        error
      });
      
      console.error('[SyncMonitor] Error en healthcheck:', error);
      
      return {
        status: 'error',
        message: error.message,
        timestamp: Date.now()
      };
    }
  }
  
  /**
   * Registra una respuesta exitosa
   * @private
   */
  _recordResponse(duration, requestData, responseData) {
    // Actualizar estadísticas
    this._recordResponseTime(duration);
    
    // Actualizar buffer para análisis
    metricsBuffer.requests.push({
      timestamp: Date.now(),
      duration,
      request: requestData,
      success: true
    });
    
    // Limitar tamaño del buffer
    if (metricsBuffer.requests.length > 100) {
      metricsBuffer.requests.shift();
    }
    
    // Notificar actualización de métricas
    this._notifyMetricsUpdate();
  }
  
  /**
   * Registra un error en petición
   * @private
   */
  _recordError(duration, requestData, errorData) {
    // Actualizar estadísticas
    this.totalErrors++;
    this._recordResponseTime(duration);
    
    // Actualizar buffer de errores
    metricsBuffer.errors.push({
      timestamp: Date.now(),
      duration,
      request: requestData,
      error: errorData
    });
    
    // Limitar tamaño del buffer
    if (metricsBuffer.errors.length > 50) {
      metricsBuffer.errors.shift();
    }
    
    // Actualizar tasa de errores
    const errorRate = this.totalRequests > 0 ? this.totalErrors / this.totalRequests : 0;
    
    // Notificar alerta si es necesario
    if (errorRate > ALERT_THRESHOLDS.errorRate) {
      this._notifyAlert({
        type: 'errorRate',
        message: `Tasa de errores alta: ${(errorRate * 100).toFixed(2)}%`,
        threshold: ALERT_THRESHOLDS.errorRate,
        value: errorRate,
        recentErrors: metricsBuffer.errors.slice(-5) // Últimos 5 errores
      });
    }
    
    // Notificar actualización de métricas
    this._notifyMetricsUpdate();
  }
  
  /**
   * Registra tiempo de respuesta
   * @private
   */
  _recordResponseTime(duration) {
    // Actualizar promedio móvil
    if (this.totalRequests === 1) {
      this.averageResponseTime = duration;
    } else {
      // Weighted moving average
      this.averageResponseTime = this.averageResponseTime * 0.9 + duration * 0.1;
    }
    
    // Actualizar buffer
    metricsBuffer.responseTimes.push({
      timestamp: Date.now(),
      duration
    });
    
    // Limitar tamaño del buffer
    if (metricsBuffer.responseTimes.length > 100) {
      metricsBuffer.responseTimes.shift();
    }
  }
  
  /**
   * Notifica alerta a todos los callbacks registrados - Patrón Observer
   * @private
   */
  _notifyAlert(alertData) {
    // Enriquecer datos de alerta
    const enrichedData = {
      ...alertData,
      timestamp: Date.now(),
      service: 'sync.nubelytics.com',
      metrics: this.getMetrics()
    };
    
    // Notificar a todos los observadores
    alertCallbacks.forEach(callback => {
      try {
        callback(enrichedData);
      } catch (e) {
        console.error('[SyncMonitor] Error en callback de alerta:', e);
      }
    });
    
    // Registrar alerta en localStorage para diagnóstico
    try {
      const alerts = JSON.parse(localStorage.getItem('nubelytics_sync_alerts') || '[]');
      alerts.push(enrichedData);
      
      // Mantener solo las últimas 20 alertas
      if (alerts.length > 20) alerts.splice(0, alerts.length - 20);
      
      localStorage.setItem('nubelytics_sync_alerts', JSON.stringify(alerts));
    } catch (e) {/* Error no crítico */}
  }
  
  /**
   * Notifica actualización de métricas - Patrón Observer
   * @private
   */
  _notifyMetricsUpdate() {
    const metrics = this.getMetrics();
    
    // Notificar a todos los observadores
    metricCallbacks.forEach(callback => {
      try {
        callback(metrics);
      } catch (e) {
        console.error('[SyncMonitor] Error en callback de métricas:', e);
      }
    });
  }
  
  /**
   * Persiste métricas en localStorage
   * @private
   */
  _persistMetrics() {
    try {
      localStorage.setItem(METRICS_STORAGE_KEY, JSON.stringify({
        lastUpdated: Date.now(),
        totalRequests: this.totalRequests,
        totalErrors: this.totalErrors,
        averageResponseTime: this.averageResponseTime,
        lastStatus: this.lastStatus,
        recentErrors: metricsBuffer.errors.slice(-5)
      }));
    } catch (e) {/* Error no crítico */}
  }
  
  /**
   * Carga métricas persistidas de localStorage
   * @private
   */
  _loadPersistedMetrics() {
    try {
      const savedMetrics = localStorage.getItem(METRICS_STORAGE_KEY);
      if (savedMetrics) {
        const metrics = JSON.parse(savedMetrics);
        
        // Solo usar métricas si son recientes (menos de 24 horas)
        const metricsAge = Date.now() - (metrics.lastUpdated || 0);
        if (metricsAge < 24 * 60 * 60 * 1000) {
          this.totalRequests = metrics.totalRequests || 0;
          this.totalErrors = metrics.totalErrors || 0;
          this.averageResponseTime = metrics.averageResponseTime || 0;
          this.lastStatus = metrics.lastStatus || null;
          
          // Restaurar errores recientes
          if (Array.isArray(metrics.recentErrors)) {
            metricsBuffer.errors = metrics.recentErrors;
          }
        }
      }
    } catch (e) {/* Error no crítico */}
  }
}

// Instancia única - Patrón Singleton
const syncMonitor = new SyncServiceMonitor();

// Función auxiliar para iniciar monitoreo automáticamente
export const initializeSyncMonitoring = (options = {}) => {
  return syncMonitor.startMonitoring(options);
};

export default syncMonitor;
